import React, { useState } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { isLoginAction } from '../../store/reducers/isOpenSlice';
import { useNavigate } from 'react-router-dom';
import { publicPostApi } from '../../api';
import Button from '../ui/Button';

// E.164-ish: optional +, then digits only; typical length 10–15
const phoneRegex = /^\+?\d{10,15}$/;

const PhoneSchema = Yup.object().shape({
	phone_number: Yup.string()
		.required('Phone number is required')
		.matches(phoneRegex, 'Enter a valid phone number (digits only, e.g. 9812345678 or +98912345678)'),
});

const ForgetPassword = ({ setIsOpenRegister }) => {
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();

	const handleLoginClick = () => {
		dispatch(isLoginAction(false));
		setIsOpenRegister ? setIsOpenRegister(false) : navigate('/login');
	};

	const handleRegisterClick = () => {
		dispatch(isLoginAction(false));
		setIsOpenRegister ? setIsOpenRegister(true) : navigate('/register');
	};

	const handleSubmit = async (values) => {
		setIsLoading(true);
		publicPostApi('system/auth/password/forget/', {
				phone_number: values.phone_number.trim(),
			})
			.then((data) => {
				const msg = data?.msg ?? data?.message ?? 'OTP sent.';
				toast.success(msg);
				setIsLoading(false);
				navigate(`/verify-otp/${encodeURIComponent(values.phone_number.trim())}`);
			})
			.catch((error) => {
				setIsLoading(false);
				toast.error(error?.message ?? 'Something went wrong. Please try again.');
			});
	};

	return (
		<div className="overflow-hidden bg-white mx-auto mt-10 md:mt-20 mb-5 md:mb-10 p-4 md:p-6 w-full md:max-w-[600px] shadow-xl rounded-lg">
			<div className="text-center mb-6">
				<h2 className="text-3xl font-bold text-black">Forgot password</h2>
				<p className="text-sm md:text-base text-gray-500 mt-2 mb-8 sm:mb-10">
					Enter your registered phone number. We’ll send you a verification code.
				</p>
			</div>

			<Formik
				validationSchema={PhoneSchema}
				initialValues={{ phone_number: '' }}
				onSubmit={handleSubmit}
			>
				{({ errors, touched }) => (
					<Form className="flex flex-col justify-center">
						<div className="grid grid-cols-1 gap-5">
							<div className="grid">
								<label
									htmlFor="phone_number"
									className="block text-gray-500 font-medium text-sm leading-none mb-2"
								>
									Phone number
								</label>
								<div className="relative">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-800">
										<svg
											stroke="currentColor"
											fill="none"
											strokeWidth="2"
											viewBox="0 0 24 24"
											className="h-5 w-5"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
										</svg>
									</div>
									<Field
										className="py-2 pl-10 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
										id="phone_number"
										name="phone_number"
										placeholder="e.g. 9812345678 or +98912345678"
										type="tel"
										autoComplete="tel"
									/>
								</div>
								{errors.phone_number && touched.phone_number && (
									<span className="text-red-400 text-sm mt-2">{errors.phone_number}</span>
								)}
							</div>
							<div className="flex items-center text-black justify-between">
								<div className="flex ms-auto">
									<button
										type="button"
										onClick={handleLoginClick}
										className="text-end text-sm text-heading ps-3 underline hover:no-underline focus:outline-none"
									>
										Login instead?
									</button>
								</div>
							</div>
							<Button type="submit" size="lg" fullWidth disabled={isLoading} className="my-1">
								{isLoading ? 'Sending…' : 'Send verification code'}
							</Button>
						</div>
					</Form>
				)}
			</Formik>
			<div className="my-8 after:bg-gray-100 before:bg-gray-100 text-center font-medium text-gray-400">
				OR
			</div>
			<div className="flex justify-between flex-col lg:flex-row gap-2">
				<button
					type="button"
					className="text-sm inline-flex items-center justify-center rounded-md text-gray-600 bg-gray-100 px-3 py-4 hover:bg-gray-200 hover:text-gray-900 transition font-semibold w-full"
				>
					<span className="ml-2">Login with Facebook</span>
				</button>
				<button
					type="button"
					className="text-sm inline-flex items-center justify-center rounded-md text-gray-600 bg-gray-100 px-3 py-4 hover:bg-gray-200 hover:text-gray-900 transition font-semibold w-full"
				>
					<span className="ml-2">Login with Google</span>
				</button>
			</div>
			<div className="text-center text-sm text-gray-900 mt-4">
				<span className="text-gray-500">Don’t have an account? </span>
				<button
					type="button"
					onClick={handleRegisterClick}
					className="text-gray-800 hover:text-brand-600 font-bold mx-1"
				>
					Register
				</button>
			</div>
		</div>
	);
};

export default ForgetPassword;
